import type { KeyboardEvent } from "react";
import { FlashcardCardFace } from "@/components/flashcards/FlashcardCardFace";
import { cn } from "@/lib/utils";

interface FlashcardCompactCardProps {
  cardNumber: number;
  front: string;
  back: string;
  isFlipped: boolean;
  onFlip: () => void;
  /** Fill a fixed grid cell instead of a fixed aspect ratio. */
  fitHeight?: boolean;
  className?: string;
}

const FRONT_TEXT_CLASS =
  "text-center text-base leading-relaxed tracking-tight text-primary [&_strong]:font-bold [&_strong]:text-primary";
const FRONT_TEXT_COMPACT_CLASS =
  "text-center text-sm leading-snug tracking-tight text-primary [&_strong]:font-bold [&_strong]:text-primary";
const BACK_TEXT_CLASS =
  "text-center text-sm leading-relaxed text-primary [&_p]:mb-2.5 [&_p:last-child]:mb-0 [&_strong]:font-bold [&_strong]:text-primary";
const BACK_TEXT_COMPACT_CLASS =
  "text-center text-xs leading-snug text-primary [&_p]:mb-1.5 [&_p:last-child]:mb-0 [&_strong]:font-bold [&_strong]:text-primary";

/**
 * Compact in-place flip card — used in the storyboard Lernkarten grid so each
 * card can be turned without opening the sequential study modal.
 */
const FlashcardCompactCard = ({
  cardNumber,
  front,
  back,
  isFlipped,
  onFlip,
  fitHeight = false,
  className,
}: FlashcardCompactCardProps) => {
  const flipLabel = isFlipped
    ? `Lernkarte ${cardNumber} zurückdrehen`
    : `Lernkarte ${cardNumber} umdrehen`;
  const density = fitHeight ? "compact" : "comfortable";

  const flipWithKeyboard = (event: KeyboardEvent) => {
    const isFlipKey = event.key === " " || event.key === "Enter";
    if (!isFlipKey) {
      return;
    }
    event.preventDefault();
    onFlip();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={flipLabel}
      onClick={onFlip}
      onKeyDown={flipWithKeyboard}
      className={cn(
        "relative w-full cursor-pointer",
        fitHeight
          ? "max-md:aspect-5/4 max-md:min-h-48 md:h-full md:min-h-0"
          : "aspect-5/4 min-h-48",
        "rounded-2xl focus:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
        className
      )}
      style={{ perspective: "1800px" }}
    >
      <div
        className={cn(
          "relative h-full w-full transform-3d transition-transform duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
          isFlipped && "transform-[rotateY(180deg)]"
        )}
      >
        <FlashcardCardFace
          text={front}
          variant="inline"
          density={density}
          textClassName={
            fitHeight ? FRONT_TEXT_COMPACT_CLASS : FRONT_TEXT_CLASS
          }
          aria-hidden={isFlipped}
        />
        <FlashcardCardFace
          text={back}
          backFace
          variant="inline"
          density={density}
          textClassName={fitHeight ? BACK_TEXT_COMPACT_CLASS : BACK_TEXT_CLASS}
          aria-hidden={!isFlipped}
        />
      </div>
    </div>
  );
};

export default FlashcardCompactCard;
